import React from 'react';
import { Link } from 'react-router-dom';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { heroImages, labSteps } from '../data/mock';
import { MessageCircle, Footprints, Monitor, LineChart, CheckCircle, ArrowRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const stepIcons = [
  MessageCircle,
  Monitor,
  Footprints,
  LineChart,
  CheckCircle
];

const stepImages = [
  '/assets/images/etape-1.webp',
  '/assets/images/etape-2.webp',
  '/assets/images/etape-3.webp',
  '/assets/images/etape-4.webp',
  '/assets/images/etape-5.webp'
];

const LeLabPage = () => {
  const { currentTheme } = useTheme();
  
  return (
    <div className="min-h-screen">
      <HeroSection
        image={heroImages.lab}
        title="Le Lab"
        subtitle="Une approche scientifique pour trouver la chaussure idéale"
        height="h-[50vh]"
      />

      {/* 5 Steps Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <SectionTitle
            title="Une chaussure à votre pied en 5 étapes"
            subtitle="Une expérience unique en France"
          />

          <div className="space-y-16 mt-12">
            {labSteps.map((step, index) => {
              const IconComponent = stepIcons[index];
              const isEven = index % 2 === 1;
              return (
                <div 
                  key={step.number} 
                  className={`grid lg:grid-cols-2 gap-8 items-center ${isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  {/* Texte */}
                  <div className={`${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-emerald-700" />
                      </div>
                      <span className="text-emerald-700 font-bold text-sm">ÉTAPE {step.number}</span>
                    </div>
                    <h3 className="font-bold text-2xl text-gray-900 mb-4">{step.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed text-justify">{step.description}</p>
                  </div>
                  {/* Image */}
                  <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="aspect-video bg-gray-200 rounded-2xl overflow-hidden shadow-lg">
                      <img 
                        src={stepImages[index]}
                        alt={`Étape ${step.number} - ${step.title}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-emerald-800 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-300 font-medium text-sm uppercase tracking-wider">Technologie footScan</span>
              <h2 className="text-3xl md:text-4xl font-neuropolitical font-bold mt-2 mb-6">
                Premier en France
              </h2>
              <p className="text-emerald-100 text-lg leading-relaxed mb-6 text-justify">
                Notre espace Lab est équipé d'une plaque de pression de 2 mètres, unique en France,
                qui analyse :
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  <span>Le profil de votre pied</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  <span>Les points de pression</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  <span>Le temps de contact au sol</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  <span>La symétrie de la foulée</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                  <span>Le déroulé du pied</span>
                </li>
              </ul>
            </div>
            <div 
              className="rounded-2xl p-8"
              style={{ 
                backgroundColor: currentTheme === 'ral6039' 
                  ? 'var(--color-secondary-500)' 
                  : 'rgba(4, 120, 87, 0.5)' 
              }}
            >
              <h3 className="font-semibold text-xl mb-4">Prix de l'analyse</h3>
              <p className={`mb-4 text-justify ${currentTheme === 'ral6039' ? 'text-white' : 'text-emerald-100'}`}>
                Le prix de l'analyse de foulée est de 40€. La prestation est offerte lors de l'achat d'une paire de chaussures
              </p>
              <p className={`text-sm ${currentTheme === 'ral6039' ? 'text-white/80' : 'text-emerald-200'}`}>
                Conseil : Apportez vos anciennes chaussures pour un diagnostic plus précis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-neuropolitical font-bold text-gray-900 mb-4">
            Prêt pour votre analyse ?
          </h2>
          <p className="text-gray-600 mb-8">
            Prenez rendez-vous dès maintenant pour découvrir votre foulée et trouver la chaussure parfaite.
          </p>
          <Link to="/rendez-vous">
            <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 group">
              Prendre RDV
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default LeLabPage;
